import React, { createContext, useCallback, useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Toast from './Toast'
import InviteModal from '../InviteModal/InviteModal'
import Modal from '../Modal'
import { useResponsive } from '../../hooks'
import { media } from '../../utils/media'
import { ROUTES, SUPER_APP_CONFIG, ERROR_CODES, CREDITS_NOT_ENOUGH_ERROR, BUTTON_LABELS } from 'shared-ui/utils/constants'
import { isAndroidApp, isCreditError } from '../../utils/helper'

const ToastContext = createContext()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  const [errorModal, setErrorModal] = useState(null)
  const [errorCode, setErrorCode] = useState('')
  const location = useLocation()
  const navigate = useNavigate()

  const addToast = useCallback((message, type = 'error', duration = 5000) => {
    // Ensure message is always a string
    let toastMessage = 'An error occurred'

    if (typeof message === 'string') {
      toastMessage = message
    } else if (message && typeof message === 'object') {
      if (message.detail) {
        toastMessage = message.detail
      } else if (message.message) {
        toastMessage = message.message
      } else {
        toastMessage = JSON.stringify(message)
      }
    } else if (message) {
      toastMessage = String(message)
    }

    const id = Date.now() + Math.random()
    const newToast = {
      id,
      message: toastMessage,
      type,
      duration,
      show: true
    }

    setToasts((prev) => [...prev, newToast])

    // Auto remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showError = useCallback((message, header) => {
    console.log('toastProvider message', message, typeof message)
    console.log('toastProvider header', header)

    let toastMessage = SUPER_APP_CONFIG?.DONT_WORRY
    let toastHeader = SUPER_APP_CONFIG?.OOPS_SOMETHING_WENT_WRONG
    let normalizedErrorCode = null

    let effectiveMsg = message
    if (typeof effectiveMsg === 'string' && effectiveMsg.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(effectiveMsg.trim())
        if (parsed && typeof parsed === 'object') {
          effectiveMsg = parsed
        }
      } catch (e) {
        // invalid JSON string, retain string
      }
    }

    if (typeof effectiveMsg === 'string' && effectiveMsg.trim() !== '') {
      toastMessage = effectiveMsg
    } else if (effectiveMsg && typeof effectiveMsg === 'object') {
      const targetObj = (effectiveMsg.error && typeof effectiveMsg.error === 'object')
        ? effectiveMsg.error
        : effectiveMsg

      normalizedErrorCode =
        targetObj.error_code ||
        targetObj.code ||
        targetObj.errorCode ||
        effectiveMsg.error_code ||
        effectiveMsg.code ||
        effectiveMsg.errorCode ||
        effectiveMsg.raw?.error_code ||
        effectiveMsg.raw?.code ||
        effectiveMsg.raw?.errorCode ||
        effectiveMsg.raw?.response?.data?.error_code ||
        effectiveMsg.raw?.response?.data?.code ||
        effectiveMsg.raw?.response?.data?.error?.error_code ||
        effectiveMsg.raw?.response?.data?.error?.code
      console.log('normalizedErrorCode', normalizedErrorCode)

      if (!normalizedErrorCode && typeof targetObj.code === 'string') {
        normalizedErrorCode = targetObj.code
      }

      setErrorCode(normalizedErrorCode || targetObj.code)

      const extractStringMsg = (obj) => {
        if (!obj) return null
        if (typeof obj.message === 'string' && obj.message) return obj.message
        if (typeof obj.error === 'string' && obj.error) return obj.error
        if (typeof obj.detail === 'string' && obj.detail) return obj.detail
        return null
      }

      const extractedMsg = extractStringMsg(targetObj) || extractStringMsg(effectiveMsg)

      if (normalizedErrorCode) {
        let lookup = normalizedErrorCode
        try {
          if (typeof lookup === 'string') lookup = lookup.toLowerCase()
          const mapped = ERROR_CODES(lookup)
          let errorData = null
          try {
            errorData = typeof mapped === 'string' ? JSON.parse(mapped) : mapped
          } catch (e) {
            errorData = mapped
          }

          let mappedMsg = typeof errorData === 'string' ? errorData : errorData?.message
          let mappedTitle = typeof errorData === 'object' ? errorData?.title : null

          if (mappedMsg && mappedMsg !== SUPER_APP_CONFIG?.DONT_WORRY) {
            toastMessage = mappedMsg
            if (mappedTitle) toastHeader = mappedTitle
          } else {
            toastMessage = extractedMsg || toastMessage
          }
        } catch (e) {
          toastMessage = extractedMsg || toastMessage
        }
      } else {
        toastMessage = extractedMsg || toastMessage
      }
    } else if (effectiveMsg != null) {
      toastMessage = String(effectiveMsg)
    }

    if (String(toastMessage).includes('[object Object]')) {
      toastMessage = SUPER_APP_CONFIG.DONT_WORRY
    }

    const isAi124Code = (c) => Boolean(c && String(c).trim().toUpperCase() === 'AI124')
    const isAi124 =
      isAi124Code(normalizedErrorCode) ||
      isAi124Code(effectiveMsg?.error_code) ||
      isAi124Code(effectiveMsg?.code) ||
      isAi124Code(effectiveMsg?.errorCode) ||
      isAi124Code(effectiveMsg?.raw?.error_code) ||
      isAi124Code(effectiveMsg?.raw?.code) ||
      isAi124Code(effectiveMsg?.raw?.response?.data?.error_code) ||
      isAi124Code(effectiveMsg?.raw?.response?.data?.code) ||
      isAi124Code(message) ||
      isAi124Code(message?.error_code) ||
      isAi124Code(message?.code)

    const creditErr =
      !isAi124 &&
      (isCreditError(message) ||
        isCreditError(effectiveMsg) ||
        isCreditError(normalizedErrorCode) ||
        isCreditError(toastMessage) ||
        (normalizedErrorCode && String(normalizedErrorCode).toUpperCase() === 'AI003'))

    if (creditErr || isAi124) {
      setErrorCode('')
    }

    if (creditErr) {
      setErrorModal({
        isCreditError: true,
        header: CREDITS_NOT_ENOUGH_ERROR?.NOT_ENOUGH_CREDITS || 'Not Enough Credits',
        message: CREDITS_NOT_ENOUGH_ERROR?.NOT_ENOUGH_CREDITS_DESC || 'You don’t have enough credits for this action. Top up now to continue creating.'
      })
      return
    }

    setErrorModal({
      message: toastMessage,
      header: typeof header === 'string' ? header : (typeof toastHeader === 'string' ? toastHeader : SUPER_APP_CONFIG.OOPS_SOMETHING_WENT_WRONG),
      isCreditError: false,
      isAi124: Boolean(isAi124)
    })
  }, [])

  const showSuccess = useCallback(
    (message, duration) => {
      return addToast(message, 'success', duration)
    },
    [addToast]
  )

  const showWarning = useCallback(
    (message, duration) => {
      return addToast(message, 'warning', duration)
    },
    [addToast]
  )

  const showInfo = useCallback(
    (message, duration) => {
      return addToast(message, 'info', duration)
    },
    [addToast]
  )

  const clearAll = useCallback(() => {
    setToasts([])
    setErrorModal(null)
  }, [])

  const handleUpgradeClick = useCallback(() => {
    setErrorModal(null)
    navigate(ROUTES.subscription)
  }, [navigate])

  const value = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    clearAll,
    addToast,
    removeToast
  }

  const isMobile = useResponsive(media.mobile)

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Render error modal */}
      {errorModal &&
        (errorModal.isCreditError ? (
          isMobile ? (
            <Modal
              open={!!errorModal}
              onClose={() => setErrorModal(null)}
              title={errorModal.header}
              cancelText={CREDITS_NOT_ENOUGH_ERROR?.UPGRADE_CREDITS || 'Upgrade'}
              onCancel={() => {
                setErrorModal(null)
                navigate(ROUTES.subscription)
              }}
              confirmText={CREDITS_NOT_ENOUGH_ERROR?.TOP_UP_CREDITS || 'Top-Up Credits'}
              onConfirm={() => {
                setErrorModal(null)
                navigate(ROUTES.subscription, {
                  state: {
                    fromWalletTopUp: 'topup'
                  }
                })
              }}
              modalStyle={{
                width: '86vw',
                padding: '18px 20px',
                borderRadius: '14px',
                background: '#050505',
                border: '1px solid #666666',
                textAlign: 'center'
              }}
            >
              <div style={{ color: '#ffffff', padding: '6px 0' }}>
                <div
                  style={{
                    marginBottom: 8,
                    color: '#ffffff',
                    fontSize: 14,
                    textAlign: 'start'
                  }}
                >
                  {errorModal.message}
                </div>
              </div>
            </Modal>
          ) : (
            <InviteModal
              header={errorModal.header}
              message={errorModal.message}
              onClick={() => {
                setErrorModal(null)
                navigate(ROUTES.subscription, {
                  state: {
                    fromWalletTopUp: 'topup'
                  }
                })
              }}
              retryButton
              confirmText={CREDITS_NOT_ENOUGH_ERROR?.UPGRADE_CREDITS || 'Upgrade'}
              cancelText={CREDITS_NOT_ENOUGH_ERROR?.TOP_UP_CREDITS || 'Top-Up Credits'}
              handleRetry={() => {
                setErrorModal(null)
                navigate(ROUTES.subscription)
              }}
              isCreditsError={true}
              onClose={() => setErrorModal(null)}
            />
          )
        ) : (
          isMobile ? (
            <Modal
              open={!!errorModal}
              onClose={() => {
                setErrorModal(null)
                if (
                  !errorModal.isAi124 &&
                  !isAndroidApp &&
                  (location?.pathname == '/updatedPayment' ||
                    location?.pathname == '/payment')
                )
                  navigate(ROUTES.subscription)
              }}
              title={errorModal.header}
              confirmText={errorModal.isAi124 ? BUTTON_LABELS?.SUBSCRIBE : undefined}
              onConfirm={errorModal.isAi124 ? handleUpgradeClick : undefined}
              cancelText={errorModal.isAi124 ? BUTTON_LABELS?.cancel : undefined}
              onCancel={errorModal.isAi124 ? () => setErrorModal(null) : undefined}
            >
              {errorModal.message}
            </Modal>
          ) : (
            <InviteModal
              header={errorModal.header}
              message={errorModal.message}
              errorCode={errorModal.isAi124 ? '' : errorCode}
              width={'430px'}
              retryButton={Boolean(errorModal.isAi124)}
              confirmText={errorModal.isAi124 ? BUTTON_LABELS?.SUBSCRIBE : undefined}
              handleRetry={errorModal.isAi124 ? handleUpgradeClick : undefined}
              cancelText={errorModal.isAi124 ? BUTTON_LABELS?.cancel : undefined}
              isSubscribeError={Boolean(errorModal.isAi124)}
              footerRowReverse={Boolean(errorModal.isAi124)}
              onClose={() => setErrorModal(null)}
              onClick={
                errorModal.isAi124
                  ? () => setErrorModal(null)
                  : () => {
                      setErrorModal(null)
                      if (
                        !isAndroidApp &&
                        (location?.pathname == '/updatedPayment' ||
                          location?.pathname == '/payment')
                      )
                        navigate(ROUTES.subscription)
                    }
              }
            />
          )
        ))}

      {/* Render toasts */}
      {toasts.filter((t) => t.type !== 'dark').length > 0 && (
        <div
          className="fixed bottom-2 left-0 right-0 pointer-events-none"
          style={{ zIndex: 10000 }}
        >
          <div className="flex flex-col items-center space-y-2 pb-4 capitalize">
            {toasts
              .filter((t) => t.type !== 'dark')
              .map((toast) => (
                <div key={toast.id} className="pointer-events-auto">
                  <Toast
                    message={toast.message}
                    type={toast.type}
                    show={toast.show}
                    duration={0}
                    onClose={() => removeToast(toast.id)}
                    position="bottom"
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Render dark bottom-right toasts */}
      {toasts.filter((t) => t.type === 'dark').length > 0 && (
        <div
          className="fixed bottom-20 right-10 pointer-events-none flex flex-col items-end space-y-2"
          style={{ zIndex: 10000 }}
        >
          {toasts
            .filter((t) => t.type === 'dark')
            .map((toast) => (
              <div key={toast.id} className="pointer-events-auto">
                <Toast
                  message={toast.message}
                  type="dark"
                  show={toast.show}
                  duration={0}
                  onClose={() => removeToast(toast.id)}
                  position="bottom-right"
                />
              </div>
            ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}
